import React, { useState } from 'react'
import { trackEvent, trackCourseSwitch } from 'utils/analytics'
import { useDiscountPreviews } from 'hooks'
import PackageSelector from '../shared/PackageSelector'
import DrillDownCourseSelect from '../shared/DrillDownCourseSelect'

const formatReplyTime = (minutes, translations) => {
  if (!minutes) return null;
  if (minutes < 60) return (translations?.replyTimeMinutes || 'Usually replies within %{count} minutes').replace('%{count}', minutes);
  const hours = Math.round(minutes / 60);
  return (translations?.replyTimeHours || 'Usually replies within %{count} hours').replace('%{count}', hours);
};

export default function TeamPriceCard({ courses, courseCategories, colors, onPurchase, onClaimTrial, onConsult, onBooking, currentUser, purchasing, bookingMode, trialUsed, ownerActivity, translations, teamId }) {
  const [selectedCourseId, setSelectedCourseId] = useState(courses?.[0]?.id || null)
  const [selectedPackageId, setSelectedPackageId] = useState(null)

  const selectedCourse = courses?.find(c => c.id === selectedCourseId)
  const trialPkg = !trialUsed ? selectedCourse?.packages?.find(p => p.isTrial) : null
  const regularPackages = selectedCourse?.packages?.filter(p => !p.isTrial) || []
  const visiblePackages = [...(trialPkg ? [trialPkg] : []), ...regularPackages]

  // Student coupon / referral discount per package (shown on the card so the
  // student sees the deal without opening checkout). Empty for logged-out visitors.
  const discountPreviews = useDiscountPreviews(teamId, !!currentUser?.id)

  // Reset package selection when course changes
  React.useEffect(() => {
    setSelectedPackageId(null)
  }, [selectedCourseId, selectedCourse?.packages])

  // 判斷學生是否有課程餘額
  const balancesWithLessons = currentUser?.balances?.filter(b => b.remainingLessons > 0) || []
  const hasBalance = balancesWithLessons.length > 0

  const handleCourseSelect = (courseId) => {
    trackCourseSwitch(courses, courseId, 'sidebar')
    setSelectedCourseId(courseId)
  }

  const handlePackageSelect = (pkgId) => {
    if (purchasing) return
    const pkg = visiblePackages.find(p => p.id === pkgId)
    if (!selectedCourse || !pkg) return
    setSelectedPackageId(pkgId)
    trackEvent('purchase_click', {
      course_name: selectedCourse.name,
      lesson_count: pkg.lessonCount,
      price: pkg.totalPrice,
      is_trial: pkg.isTrial || false,
      // Did the buyer stay on the course the card opened with, or switch first?
      // course_name alone cannot answer that without knowing the default.
      is_default_course: selectedCourse.id === courses?.[0]?.id,
      source: 'price_card'
    })
    if (pkg.isTrial && pkg.totalPrice === 0) {
      onClaimTrial?.(selectedCourse)
    } else {
      onPurchase?.(selectedCourse, pkg)
    }
  }

  if (!courses || courses.length === 0) {
    return null
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sticky top-6">
      {/* Booking Section - 有餘額的學生才顯示 */}
      {hasBalance && (
        <>
          <div className="rounded-xl p-4 mb-4" style={{ background: 'linear-gradient(135deg, #EEF4FF 0%, #E0ECFF 100%)' }}>
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5" style={{ color: colors.primary }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <h3 className="text-sm font-bold" style={{ color: colors.primary }}>
                {translations?.myLessonBalance || 'My Lesson Balance'}
              </h3>
            </div>
            <div className="space-y-2">
              {balancesWithLessons.map((balance) => (
                <div
                  key={balance.courseId}
                  className="flex items-center justify-between bg-white/80 backdrop-blur-sm px-3 py-2.5 rounded-lg"
                >
                  <span className="text-sm font-medium text-gray-700">{balance.courseName}</span>
                  <div className="flex items-baseline gap-1">
                    <span className="text-lg font-bold" style={{ color: colors.primary }}>
                      {balance.remainingLessons}
                    </span>
                    <span className="text-xs text-gray-500">{translations?.lessonsUnit || 'lesson(s)'}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {bookingMode === 'teacher_managed' ? (
            <div className="text-center py-3 mb-1">
              <div className="flex items-center justify-center gap-1.5 text-sm font-medium" style={{ color: colors.primary }}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {translations?.teacherWillSchedule || 'Your teacher will schedule lessons for you'}
              </div>
              <p className="text-xs mt-1" style={{ color: '#717171' }}>
                {translations?.pleaseWaitNotification || 'Please wait for a notification'}
              </p>
            </div>
          ) : (
            <>
              <button
                onClick={() => onBooking && onBooking()}
                className="w-full py-3.5 rounded-xl font-semibold text-white shadow-md hover:shadow-lg transition-all mb-1 cursor-pointer"
                style={{ background: colors.primaryGradient }}
              >
                {translations?.bookNow || 'Book Now'}
              </button>
              <p className="text-xs text-center mb-4" style={{ color: '#717171' }}>
                {translations?.bookNowDescription || 'Choose a teacher and time to use your lesson balance'}
              </p>
            </>
          )}

          <div className="border-t border-gray-100 my-4" />
        </>
      )}

      {/* Course Selector */}
      {courses.length > 1 && (
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2" style={{ color: '#222222' }}>
            {translations?.selectCourse || 'Select Course'}
          </label>
          <DrillDownCourseSelect
            courses={courses}
            courseCategories={courseCategories}
            selectedCourseId={selectedCourseId}
            onSelect={handleCourseSelect}
            colors={colors}
            translations={translations}
          />
        </div>
      )}

      {/* Package Options */}
      {visiblePackages.length > 0 && (
        <div className="mb-4">
          {/* No heading here on purpose. The course name and the count above it
              are what the price belongs to, and a heading between them pushed
              the two apart. */}
          <PackageSelector
            key={selectedCourse?.id}
            packages={visiblePackages}
            courseDuration={selectedCourse?.durationMinutes}
            selectedId={selectedPackageId}
            onSelect={handlePackageSelect}
            trialUsed={trialUsed}
            colors={colors}
            translations={translations}
            discountPreviews={discountPreviews}
          />
        </div>
      )}

      {/* Average Reply Time */}
      {ownerActivity?.averageReplyMinutes > 0 && (
        <p className="text-xs text-center text-gray-400 py-2">
          {formatReplyTime(ownerActivity.averageReplyMinutes, translations)}
        </p>
      )}

      {/* Consult Button */}
      {onConsult && (
        <button
          onClick={() => onConsult({ source: 'price_card' })}
          className="w-full py-3.5 rounded-xl font-semibold transition-colors cursor-pointer hover:opacity-90 border"
          style={{ backgroundColor: colors?.background || '#E6F0FF', color: colors?.primaryDark || '#2952CC', borderColor: colors?.primaryLight || '#6699FF' }}
        >
          {translations?.freeConsultation || 'Contact a Teacher'}
        </button>
      )}

      {/* Info */}
      <p className="text-xs text-center mt-4" style={{ color: '#717171' }}>
        {hasBalance
          ? (bookingMode === 'teacher_managed' ? (translations?.teacherScheduleAfterPurchase || 'Your teacher will schedule lessons after purchase') : (translations?.chooseTeacherCompleteBooking || 'Choose a teacher and time to complete your booking'))
          : (translations?.afterPaymentInfo || 'You can start scheduling lessons after payment')
        }
      </p>
    </div>
  )
}
